import { parseError } from '../helpers/error';
import {
  delete_collection,
  create_collection,
  create_food_category,
  create_subcategory,
  update_subcategory,
  create_department,
  update_department,
  delete_department,
  update_food_category,
  create_product_brand,
  create_product_group,
  update_product_group,
  update_collection,
  categoriesApi,
} from '../api/categories.api';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAppSelector } from './redux.hooks';
import { authSelector } from '../store/slices';

export const useMenu = () => {
  const queryClient = useQueryClient();
  const { user } = useAppSelector(authSelector);

  // derive brand IDs from authenticated user (defensive shape handling)
  const brandIds: number[] = (() => {
    const brandsField = (user as any)?.brands;
    if (!brandsField) return [];
    if (Array.isArray(brandsField) && brandsField.length > 0) {
      if (typeof brandsField[0] === 'number') return brandsField as number[];
      if (typeof brandsField[0] === 'object' && brandsField[0].id)
        return brandsField.map((b: any) => b.id);
    }
    return [];
  })();

  // Mutation for deleting a collection with optimistic update and error handling
  const deleteCollectionMutation = useMutation(
    (collectionId: string) => delete_collection(collectionId),
    {
      onMutate: async (collectionId: string) => {
        await queryClient.cancelQueries(['collections']);
        const previous = queryClient.getQueryData(['collections']);
        // optimistic remove
        queryClient.setQueryData(['collections'], (old: any) =>
          (old || []).filter((c: any) => c.id !== collectionId)
        );
        return { previous };
      },
      onError: (err: any, _id, context: any) => {
        toast.error(parseError(err));
        if (context?.previous)
          queryClient.setQueryData(['collections'], context.previous);
      },
      onSuccess: () => {
        toast.success('Collection deleted successfully');
        queryClient.invalidateQueries(['collections']);
      },
    }
  );

  const deleteCollection = (collectionId: string) => {
    if (window.confirm('Are you sure you want to delete this collection?')) {
      deleteCollectionMutation.mutate(collectionId);
    }
  };

  // Mutation for creating a collection
  const createCollectionMutation = useMutation(
    (params: any) => create_collection(params),
    {
      onSuccess: (res: any) => {
        toast.success('Collection created successfully');
        queryClient.invalidateQueries(['collections']);
        queryClient.invalidateQueries(['product_groups']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const createCollection = async (params: any) => {
    try {
      return await createCollectionMutation.mutateAsync(params);
    } catch (err) {
      throw err;
    }
  };

  // Mutation for creating a product brand
  const createProductBrandMutation = useMutation(
    (params: any) => create_product_brand(params),
    {
      onSuccess: (res: any) => {
        toast.success('Product brand created successfully');
        queryClient.invalidateQueries(['product-brands']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const createProductBrand = async (params: any) => {
    try {
      return await createProductBrandMutation.mutateAsync(params);
    } catch (err) {
      throw err;
    }
  };

  // Mutation for creating a product group
  const createProductGroupMutation = useMutation(
    (params: any) => create_product_group(params),
    {
      onSuccess: (res: any) => {
        toast.success('Product group created successfully');
        queryClient.invalidateQueries(['product_groups']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const createProductGroup = async (params: any) => {
    try {
      return await createProductGroupMutation.mutateAsync(params);
    } catch (err) {
      throw err;
    }
  };

  // Mutation for updating a product group
  const updateProductGroupMutation = useMutation(
    (payload: { groupId: string; params: any }) =>
      update_product_group(payload.groupId, payload.params),
    {
      onSuccess: (res: any) => {
        toast.success('Product group updated successfully');
        queryClient.invalidateQueries(['product_groups']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const updateProductGroup = async (groupId: string, params: any) => {
    try {
      return await updateProductGroupMutation.mutateAsync({ groupId, params });
    } catch (err) {
      throw err;
    }
  };

  // Mutation for updating a collection
  const updateCollectionMutation = useMutation(
    (payload: { collectionId: string; params: any }) =>
      update_collection(payload.collectionId, payload.params),
    {
      onSuccess: (res: any) => {
        toast.success('Collection updated successfully');
        queryClient.invalidateQueries(['collections']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const updateCollection = async (collectionId: string, params: any) => {
    try {
      return await updateCollectionMutation.mutateAsync({
        collectionId,
        params,
      });
    } catch (err) {
      throw err;
    }
  };

  // Mutation for creating a category
  const createCategoryMutation = useMutation(
    (params: any) => {
      const payload =
        !params?.brands && brandIds.length > 0
          ? { ...params, brands: brandIds }
          : params;
      return create_food_category(payload);
    },
    {
      onSuccess: (res: any) => {
        toast.success('Category created successfully');
        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['all-categories']);
        queryClient.invalidateQueries(['brand-categories']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const createCategory = async (params: any) => {
    try {
      return await createCategoryMutation.mutateAsync(params);
    } catch (err) {
      throw err;
    }
  };

  // Mutation for creating a subcategory
  const createSubcategoryMutation = useMutation(
    (params: any) =>
      create_subcategory({ ...params, brands: brandIds as number[] }),
    {
      onSuccess: (res: any) => {
        toast.success('Subcategory created successfully');
        queryClient.invalidateQueries(['subcategories']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const createSubcategory = async (params: any) => {
    try {
      return await createSubcategoryMutation.mutateAsync(params);
    } catch (err) {
      throw err;
    }
  };

  // Mutation for creating a department
  const createDepartmentMutation = useMutation(
    (params: any) => create_department(params),
    {
      onSuccess: (res: any) => {
        toast.success('Department created successfully');
        queryClient.invalidateQueries(['departments']);
        // Also invalidate category queries since departments and categories are related
        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['all-categories']);
        queryClient.invalidateQueries(['brand-categories']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const createDepartment = async (params: any) => {
    try {
      return await createDepartmentMutation.mutateAsync(params);
    } catch (err) {
      throw err;
    }
  };

  // Mutation for deleting a department
  const deleteDepartmentMutation = useMutation(
    (id: string) => delete_department(id),
    {
      onSuccess: () => {
        toast.success('Department deleted successfully');
        queryClient.invalidateQueries(['departments']);
        // Also invalidate category queries since departments and categories are related
        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['all-categories']);
        queryClient.invalidateQueries(['brand-categories']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const deleteDepartment = async (id: string) => {
    try {
      return await deleteDepartmentMutation.mutateAsync(id);
    } catch (err) {
      throw err;
    }
  };

  // Mutation for deleting a category (mark as INACTIVE)
  const deleteCategoryMutation = useMutation(
    async (id: number) => {
      const existing = await categoriesApi.getCategoryById(id);
      if (!existing) throw new Error('Category not found');
      const deleteParams = {
        name: existing.name,
        description: existing.description || '',
        status: 'INACTIVE',
      };
      return await update_food_category(id, deleteParams);
    },
    {
      onSuccess: () => {
        toast.success('Category marked as inactive');
        queryClient.invalidateQueries(['all-categories']);
        queryClient.invalidateQueries(['categories']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const deleteCategory = async (id: number) => {
    try {
      return await deleteCategoryMutation.mutateAsync(id);
    } catch (err) {
      throw err;
    }
  };

  // Mutation for bulk deleting categories
  const bulkDeleteCategoriesMutation = useMutation(
    async (ids: number[]) => {
      return await categoriesApi.bulkDeleteCategories(ids);
    },
    {
      onSuccess: (_, ids) => {
        toast.success(`${ids.length} ${ids.length === 1 ? 'category' : 'categories'} deleted`);
        queryClient.invalidateQueries(['all-categories']);
        queryClient.invalidateQueries(['categories']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const bulkDeleteCategories = async (ids: number[]) => {
    if (ids.length === 0) return;
    return await bulkDeleteCategoriesMutation.mutateAsync(ids);
  };

  // Mutation for updating a department
  const updateDepartmentMutation = useMutation(
    (payload: { id: string; params: any }) =>
      update_department(payload.id, payload.params),
    {
      onSuccess: (res: any) => {
        toast.success('Department updated successfully');
        queryClient.invalidateQueries(['departments']);
        // Also invalidate category queries since departments and categories are related
        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['all-categories']);
        queryClient.invalidateQueries(['brand-categories']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const updateDepartment = async (id: string, params: any) => {
    try {
      return await updateDepartmentMutation.mutateAsync({ id, params });
    } catch (err) {
      throw err;
    }
  };

  // Mutation for updating a category
  const updateCategoryMutation = useMutation(
    (payload: { categoryId: number; params: any }) =>
      update_food_category(payload.categoryId, payload.params),
    {
      onSuccess: (res: any, variables: any) => {
        toast.success('Category updated successfully');
        // Invalidate all category-related queries
        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['all-categories']);
        queryClient.invalidateQueries(['brand-categories']);
        // Invalidate the specific category-by-id cache so fresh data is fetched
        if (variables?.categoryId) {
          queryClient.invalidateQueries([
            'get-category-by-id',
            variables.categoryId,
          ]);
        }
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const updateCategory = async (categoryId: number, params: any) => {
    try {
      return await updateCategoryMutation.mutateAsync({ categoryId, params });
    } catch (err) {
      throw err;
    }
  };

  // Mutation for updating a subcategory
  const updateSubcategoryMutation = useMutation(
    (payload: { id: number; params: any }) =>
      update_subcategory(payload.id, payload.params),
    {
      onSuccess: (res: any) => {
        toast.success('Subcategory updated successfully');
        queryClient.invalidateQueries(['subcategories']);
      },
      onError: (err: any) => {
        toast.error(parseError(err));
      },
    }
  );

  const updateSubcategory = async (id: number, params: any) => {
    try {
      return await updateSubcategoryMutation.mutateAsync({ id, params });
    } catch (err) {
      throw err;
    }
  };

  return {
    deleteCollection,
    createCollection,
    createCategory,
    createSubcategory,
    createProductBrand,
    createProductGroup,
    updateProductGroup,
    updateCollection,
    deleteCollectionLoading: deleteCollectionMutation.isLoading,
    createCollectionLoading: createCollectionMutation.isLoading,
    createCategoryLoading: createCategoryMutation.isLoading,
    createSubcategoryLoading: createSubcategoryMutation.isLoading,
    createDepartment,
    createDepartmentLoading: createDepartmentMutation.isLoading,
    createProductBrandLoading: createProductBrandMutation.isLoading,
    createProductGroupLoading: createProductGroupMutation.isLoading,
    updateCategory,
    updateCategoryLoading: updateCategoryMutation.isLoading,
    updateProductGroupLoading: updateProductGroupMutation.isLoading,
    updateCollectionLoading: updateCollectionMutation.isLoading,
    updateSubcategory,
    updateSubcategoryLoading: updateSubcategoryMutation.isLoading,
    updateDepartment,
    updateDepartmentLoading: updateDepartmentMutation.isLoading,
    deleteDepartment,
    deleteDepartmentLoading: deleteDepartmentMutation.isLoading,
    deleteCategory,
    deleteCategoryLoading: deleteCategoryMutation.isLoading,
    bulkDeleteCategories,
    bulkDeleteCategoriesLoading: bulkDeleteCategoriesMutation.isLoading,
  };
};
